import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import { useToast, ToastProvider } from "@/components/base/Toast";
import { LoadingState } from "@/components/base/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import { STRONG_PASSWORD, STRONG_PASSWORD_HINT } from "@/services/authService";

/**
 * Mot de passe provisoire (compte ouvert par le super-administrateur ou réinitialisé par un
 * administrateur) : il doit être remplacé avant tout accès. La session est ensuite close.
 */
function ChangePasswordForm() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user, loading: sessionLoading, changePassword, logout } = useAuth();
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  if (sessionLoading) return <LoadingState label="Ouverture de votre session..." />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.mustChangePassword) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!STRONG_PASSWORD.test(pwd.next)) {
      notify(`Nouveau mot de passe trop faible : ${STRONG_PASSWORD_HINT}.`, "error");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      notify("La confirmation ne correspond pas au nouveau mot de passe.", "error");
      return;
    }
    if (pwd.next === pwd.current) {
      notify("Le nouveau mot de passe doit être différent du mot de passe provisoire.", "error");
      return;
    }
    setSaving(true);
    try {
      await changePassword(pwd.current, pwd.next);
      navigate("/login", {
        replace: true,
        state: { notice: "Mot de passe changé. Connectez-vous avec votre nouveau mot de passe." },
      });
    } catch (err) {
      notify(err instanceof Error ? err.message : "Changement impossible.", "error");
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-6 py-12">
      <div className="w-full max-w-md animate-slide-up rounded-lg border border-background-200 bg-background-50 p-8">
        <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
          <i className="ri-lock-password-line text-xl"></i>
        </span>
        <h1 className="text-xl font-semibold font-heading text-foreground-950">
          Choisissez votre mot de passe
        </h1>
        <p className="mt-1 text-sm text-foreground-500">
          Bonjour {user.firstName}, votre compte utilise un mot de passe provisoire. Remplacez-le par
          un mot de passe personnel pour accéder à votre espace.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <Input
            label="Mot de passe provisoire"
            type="password"
            autoComplete="current-password"
            value={pwd.current}
            onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
            required
          />
          <Input
            label="Nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            value={pwd.next}
            onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            hint={STRONG_PASSWORD_HINT}
            required
          />
          <Input
            label="Confirmer le nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            value={pwd.confirm}
            onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
            required
          />
          <Button type="submit" size="lg" className="w-full" icon="ri-shield-check-line" disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer et me reconnecter"}
          </Button>
          <button
            type="button"
            onClick={() => void logout().then(() => navigate("/login", { replace: true }))}
            className="w-full text-center text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer"
          >
            Se déconnecter
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ChangePasswordPage() {
  return (
    <ToastProvider>
      <ChangePasswordForm />
    </ToastProvider>
  );
}
